// GeoTIFF auto-placement for drone maps (§228 follow-up). Parses a GeoTIFF entirely in the
// browser (geotiff.js), derives its lat/lng footprint (reprojecting via proj4 — handles
// EPSG:4326, 3857, and WGS84 UTM), and rasterizes it to a downscaled JPEG. So we store a
// viewable overlay + auto-derived bounds without a server-side GDAL/tiling pipeline and within
// the 2 MB upload cap. Both deps are dynamic-imported (kept out of the main bundle).
//
// Placement note: the JPEG is drawn as an L.imageOverlay at the 4326 bounding box. For a
// north-up ortho at farm scale the UTM↔lat/lng non-linearity is sub-metre, so this is accurate
// enough; per-pixel-correct reprojection (georaster-layer-for-leaflet) is a further follow-up.

export interface GeoBounds { south: number; west: number; north: number; east: number }
export interface ParsedGeoTIFF { bounds: GeoBounds; blob: Blob }

export function isGeoTIFF(file: File): boolean {
  return /\.tiff?$/i.test(file.name) || file.type === 'image/tiff'
}

// utmProjString builds a proj4 definition for a WGS84 UTM EPSG code (326xx = north, 327xx = south).
function utmProjString(epsg: number): string | null {
  if (epsg >= 32601 && epsg <= 32660) return `+proj=utm +zone=${epsg - 32600} +datum=WGS84 +units=m +no_defs`
  if (epsg >= 32701 && epsg <= 32760) return `+proj=utm +zone=${epsg - 32700} +south +datum=WGS84 +units=m +no_defs`
  return null
}

function boundsToWGS84(epsg: number, minX: number, minY: number, maxX: number, maxY: number, proj4: any): GeoBounds {
  if (epsg === 4326 || epsg === 4269) return { west: minX, south: minY, east: maxX, north: maxY }
  let def: string
  if (epsg === 3857 || epsg === 900913 || epsg === 102100) def = 'EPSG:3857'
  else {
    const u = utmProjString(epsg)
    if (!u) throw new Error(`unsupported GeoTIFF projection (EPSG:${epsg}) — export as WGS84/UTM/Web-Mercator`)
    def = u
  }
  // Convert all 4 corners → lat/lng, take the min/max (a north-up ortho's corners bound the extent).
  const corners: [number, number][] = [[minX, minY], [minX, maxY], [maxX, minY], [maxX, maxY]]
  const ll = corners.map(c => proj4(def, 'EPSG:4326', c) as [number, number])
  const lons = ll.map(p => p[0]), lats = ll.map(p => p[1])
  return { west: Math.min(...lons), east: Math.max(...lons), south: Math.min(...lats), north: Math.max(...lats) }
}

async function rasterizeToJpeg(image: any): Promise<Blob> {
  const w0 = image.getWidth(), h0 = image.getHeight()
  const maxDim = 2000
  let w = w0, h = h0
  if (Math.max(w, h) > maxDim) { const s = maxDim / Math.max(w, h); w = Math.max(1, Math.round(w * s)); h = Math.max(1, Math.round(h * s)) }
  const spp = image.getSamplesPerPixel()
  // Read at the reduced resolution (geotiff.js decimates → we never decode full-res).
  const raster: any = await image.readRasters({ width: w, height: h, interleave: true })
  // Normalize to 0–255 if the samples aren't already 8-bit.
  let scale = 1
  if (!(raster instanceof Uint8Array || raster instanceof Uint8ClampedArray)) {
    let max = 0
    for (let i = 0; i < raster.length; i++) if (raster[i] > max) max = raster[i]
    scale = max > 0 ? 255 / max : 1
  }
  const canvas = document.createElement('canvas')
  canvas.width = w; canvas.height = h
  const ctx = canvas.getContext('2d')!
  const img = ctx.createImageData(w, h)
  for (let i = 0; i < w * h; i++) {
    let r: number, g: number, b: number, a = 255
    if (spp === 1) { r = g = b = raster[i] * scale }
    else {
      r = raster[i * spp] * scale
      g = raster[i * spp + 1] * scale
      b = raster[i * spp + 2] * scale
      if (spp >= 4) a = raster[i * spp + 3] // alpha (nodata) as-is; JPEG flattens it to black
    }
    img.data[i * 4] = r; img.data[i * 4 + 1] = g; img.data[i * 4 + 2] = b; img.data[i * 4 + 3] = a
  }
  ctx.putImageData(img, 0, 0)
  for (const q of [0.85, 0.7, 0.55, 0.4]) {
    const blob: Blob | null = await new Promise(res => canvas.toBlob(res, 'image/jpeg', q))
    if (blob && blob.size < 1_900_000) return blob
  }
  const fallback: Blob | null = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.3))
  if (!fallback) throw new Error('could not render the GeoTIFF')
  return fallback
}

// parseGeoTIFF returns the ortho's auto-derived lat/lng bounds + a downscaled JPEG to upload.
export async function parseGeoTIFF(file: File): Promise<ParsedGeoTIFF> {
  const [{ fromBlob }, proj4mod] = await Promise.all([import('geotiff'), import('proj4')])
  const proj4: any = (proj4mod as any).default || proj4mod
  const tiff = await fromBlob(file)
  const image = await tiff.getImage()
  const bbox = image.getBoundingBox() // [minX, minY, maxX, maxY] in the file CRS
  const keys: any = image.getGeoKeys() || {}
  const epsg: number = keys.ProjectedCSTypeGeoKey || keys.GeographicTypeGeoKey || 4326
  const bounds = boundsToWGS84(epsg, bbox[0], bbox[1], bbox[2], bbox[3], proj4)
  if (!isFinite(bounds.south) || !isFinite(bounds.west) || bounds.north <= bounds.south || bounds.east <= bounds.west) {
    throw new Error('the GeoTIFF has no usable georeferencing')
  }
  const blob = await rasterizeToJpeg(image)
  return { bounds, blob }
}
